"use client";

import { CheckIcon, XIcon } from "lucide-react";
import { useId } from "react";

import { copyFor } from "@/lib/onboarding/copy";

import type { StepProps } from "../types";
import { ContinueButton, NumberedItem, StepFooter, StepHeader } from "../ui";

const KICKER = "How Medceptor works";

export function HowItWorksStep({ state, advance }: StepProps) {
  const track = state.answers.track;
  const gradeSheetId = useId();

  // No track yet (a resumed draft can lack it): never guess a profession's
  // copy; show only the header and let the flow move on.
  if (!track) {
    return (
      <>
        <StepHeader title={KICKER} />
        <StepFooter>
          <ContinueButton onClick={() => advance()}>Continue</ContinueButton>
        </StepFooter>
      </>
    );
  }

  const c = copyFor(track);

  return (
    <>
      <StepHeader kicker={KICKER} title={c.howItWorks.title} />
      <ol className="mt-7 flex flex-col gap-5">
        {c.howItWorks.steps.map((step, i) => (
          <NumberedItem key={step.title} n={i + 1} title={step.title}>
            {step.detail}
          </NumberedItem>
        ))}
      </ol>
      <section
        aria-labelledby={gradeSheetId}
        className="mt-7 flex flex-col gap-2.5 rounded-2xl border border-border bg-muted/40 p-4"
      >
        <p
          id={gradeSheetId}
          className="text-[11px] font-semibold tracking-[0.1em] text-muted-foreground uppercase"
        >
          {c.howItWorks.sampleHeading ?? "Sample grade sheet"}
        </p>
        <ul className="flex flex-col gap-2.5">
          {c.howItWorks.sampleLines.map((line) => (
            <li
              key={line.label}
              className="flex items-center justify-between gap-3"
            >
              <span className="font-mono text-sm text-muted-foreground">
                {line.label}
              </span>
              {line.passed ? (
                <span className="flex items-center">
                  <CheckIcon
                    aria-hidden="true"
                    className="size-4 text-emerald-600 dark:text-emerald-400"
                  />
                  <span className="sr-only">Passed</span>
                </span>
              ) : (
                <span className="flex items-center">
                  <XIcon aria-hidden="true" className="size-4 text-destructive" />
                  <span className="sr-only">Missed</span>
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>
      <StepFooter>
        <ContinueButton onClick={() => advance()}>Continue</ContinueButton>
      </StepFooter>
    </>
  );
}
